import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["form", "count", "submitBtn", "submitBtnMobile", "summary", "cartList", "cartEmpty", "cartCount", "templateSelect"]
  static values = {
    preselect: { type: Object, default: {} }
  }

  connect() {
    this.nextPosition = 0
    this.updateCount()

    // Pre-select items if data is provided (edit/template prefill/assignment→log)
    if (Object.keys(this.preselectValue).length > 0) {
      // Defer to allow all tag-toggle controllers to connect first
      requestAnimationFrame(() => this._preselectItems())
    }
  }

  loadTemplate(event) {
    const templateId = event.target.value
    const url = new URL(window.location.href)
    if (templateId) {
      url.searchParams.set("template_id", templateId)
    } else {
      url.searchParams.delete("template_id")
    }
    window.Turbo.visit(url.toString())
  }

  updateCount() {
    const form = this.formTarget
    const asanaInputs = form.querySelectorAll('input[name^="selected_asanas"]')
    const exerciseInputs = form.querySelectorAll('input[name^="selected_exercises"]')

    // Each selected item has 2 hidden inputs (reps + sets), so divide by 2
    const selectedCount = (asanaInputs.length + exerciseInputs.length) / 2

    // Update all count targets (there may be multiple: sidebar + mobile)
    this.countTargets.forEach(el => el.textContent = selectedCount)

    // Update all cart count targets
    if (this.hasCartCountTarget) {
      this.cartCountTargets.forEach(el => el.textContent = selectedCount)
    }

    // Update submit buttons
    this.submitBtnTargets.forEach(btn => btn.disabled = selectedCount === 0)
    if (this.hasSubmitBtnMobileTarget) {
      this.submitBtnMobileTargets.forEach(btn => btn.disabled = selectedCount === 0)
    }

    // Update empty state
    if (this.hasCartEmptyTarget) {
      this.cartEmptyTargets.forEach(el => {
        el.classList.toggle("hidden", selectedCount > 0)
      })
    }
    if (this.hasCartListTarget) {
      this.cartListTargets.forEach(el => {
        el.classList.toggle("hidden", selectedCount === 0)
      })
    }
  }

  syncCart(event) {
    const { active, type, id, name, color, reps, sets, supportsReps, supportsSets } = event.detail

    if (active) {
      // Set position on the hidden input for this item
      const paramType = type === "asana" ? "asanas" : "exercises"
      const posInput = this.formTarget.querySelector(`input[name="selected_${paramType}[${id}][position]"]`)
      if (posInput) posInput.value = this.nextPosition++

      this._addToCart({ type, id, name, color, reps, sets, supportsReps, supportsSets })
    } else {
      this._removeFromCart(type, id)
    }
  }

  updateCartItem(event) {
    const { type, id, reps, sets } = event.detail
    const cartItemId = `cart-item-${type}-${id}`

    // Update in all cart lists
    this.cartListTargets.forEach(cartList => {
      const item = cartList.querySelector(`[data-cart-item-id="${cartItemId}"]`)
      if (!item) return

      const repsEl = item.querySelector("[data-cart-reps]")
      const setsEl = item.querySelector("[data-cart-sets]")
      if (repsEl) repsEl.textContent = `${reps} reps`
      if (setsEl) setsEl.textContent = `${sets} sets`
    })
  }

  removeFromCart(event) {
    const type = event.currentTarget.dataset.itemType
    const id = parseInt(event.currentTarget.dataset.itemId)

    // Find the corresponding tag-toggle controller and deactivate it
    const selector = `[data-tag-toggle-type-value="${type}"][data-tag-toggle-id-value="${id}"]`
    const tagElement = this.element.querySelector(selector)
    if (tagElement) {
      const controller = this.application.getControllerForElementAndIdentifier(tagElement, "tag-toggle")
      if (controller) {
        controller.deactivateFromCart()
      }
    }
  }

  _addToCart({ type, id, name, color, reps, sets, supportsReps, supportsSets }) {
    const cartItemId = `cart-item-${type}-${id}`
    const colorMap = {
      indigo: { bg: "bg-indigo-50", text: "text-indigo-600", badge: "bg-indigo-100 text-indigo-600" },
      purple: { bg: "bg-purple-50", text: "text-purple-600", badge: "bg-purple-100 text-purple-600" },
      emerald: { bg: "bg-emerald-50", text: "text-emerald-600", badge: "bg-emerald-100 text-emerald-600" }
    }
    const colors = colorMap[color] || colorMap.indigo

    let meta = ""
    if (supportsReps) meta += `<span data-cart-reps class="text-xs text-gray-400">${reps} reps</span>`
    if (supportsReps && supportsSets) meta += `<span class="text-xs text-gray-300">&middot;</span>`
    if (supportsSets) meta += `<span data-cart-sets class="text-xs text-gray-400">${sets} sets</span>`

    const html = `
      <div data-cart-item-id="${cartItemId}" class="cart-item-enter flex items-center justify-between py-2 px-3 rounded-xl ${colors.bg} group">
        <div class="flex items-center gap-2 min-w-0">
          <span class="text-xs font-medium ${colors.text} truncate">${this._escapeHtml(name)}</span>
          ${meta ? `<div class="flex items-center gap-1">${meta}</div>` : ""}
        </div>
        <button type="button"
                class="shrink-0 w-5 h-5 rounded-md flex items-center justify-center text-gray-300
                       hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                data-action="practice-log#removeFromCart"
                data-item-type="${type}"
                data-item-id="${id}">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
    `

    this.cartListTargets.forEach(cartList => {
      cartList.insertAdjacentHTML("beforeend", html)
    })
  }

  _removeFromCart(type, id) {
    const cartItemId = `cart-item-${type}-${id}`
    this.cartListTargets.forEach(cartList => {
      const item = cartList.querySelector(`[data-cart-item-id="${cartItemId}"]`)
      if (item) {
        item.style.transition = "opacity 0.15s, transform 0.15s"
        item.style.opacity = "0"
        item.style.transform = "translateX(10px)"
        setTimeout(() => item.remove(), 150)
      }
    })
  }

  _escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  _preselectItems() {
    const data = this.preselectValue
    for (const [type, items] of Object.entries(data)) {
      for (const [id, attrs] of Object.entries(items)) {
        const selector = `[data-tag-toggle-type-value="${type}"][data-tag-toggle-id-value="${id}"]`
        const tagElement = this.element.querySelector(selector)
        if (tagElement) {
          const controller = this.application.getControllerForElementAndIdentifier(tagElement, "tag-toggle")
          if (controller && !controller.activeValue) {
            if (attrs.reps) controller.repsValue = attrs.reps
            if (attrs.sets) controller.setsValue = attrs.sets
            controller.toggle()
          }
        }
      }
    }
  }
}
